'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import WorldwayNav from '@/components/WorldwayNav';

const DEPARTURES = [
  { name: 'Highlights of Japan', region: 'Asia', duration: '14 days', groupSize: 'Max 12', departure: 'March 2027', price: 'From $12,800 pp', tags: ['Culture', 'Temples', 'Cuisine'] },
  { name: 'Patagonia Wilderness', region: 'South America', duration: '12 days', groupSize: 'Max 10', departure: 'November 2026', price: 'From $9,400 pp', tags: ['Adventure', 'Trekking', 'Wildlife'] },
  { name: 'Morocco Imperial Cities', region: 'Africa', duration: '10 days', groupSize: 'Max 12', departure: 'October 2026', price: 'From $6,200 pp', tags: ['Culture', 'Heritage', 'Souks'] },
  { name: 'Norwegian Fjords & Arctic', region: 'Europe', duration: '11 days', groupSize: 'Max 10', departure: 'June 2027', price: 'From $8,900 pp', tags: ['Nature', 'Arctic', 'Fjords'] },
  { name: 'India: Rajasthan & Beyond', region: 'Asia', duration: '15 days', groupSize: 'Max 12', departure: 'February 2027', price: 'From $7,600 pp', tags: ['Culture', 'Palaces', 'Cuisine'] },
  { name: 'Peru & Machu Picchu', region: 'South America', duration: '12 days', groupSize: 'Max 10', departure: 'September 2026', price: 'From $8,100 pp', tags: ['Heritage', 'Inca', 'Andes'] },
];

const FEATURES = [
  { icon: '👥', title: 'Intimate Groups', desc: 'Maximum 12 guests — never a crowd, always a community of like-minded travellers.' },
  { icon: '🎓', title: 'Expert Guides', desc: 'Local specialists, historians and naturalists who bring destinations to life.' },
  { icon: '🏨', title: 'Luxury Accommodation', desc: 'Boutique hotels, heritage properties and lodges — all personally vetted.' },
  { icon: '✈', title: 'Seamless Logistics', desc: 'All flights, transfers and internal travel arranged and included.' },
  { icon: '🍽', title: 'Curated Dining', desc: 'Private dinners, cooking classes and exclusive restaurant access.' },
  { icon: '🔒', title: 'Private Access', desc: 'After-hours museum visits, private palace tours and exclusive experiences.' },
];

const REGIONS = ['All Regions', 'Asia', 'Europe', 'Africa', 'South America', 'Middle East', 'Oceania'];

export default function SmallGroupPage() {
  const router = useRouter();
  const [activeRegion, setActiveRegion] = useState('All Regions');

  const filtered = activeRegion === 'All Regions'
    ? DEPARTURES
    : DEPARTURES.filter((d) => d.region === activeRegion);

  const handleBook = (name: string) => {
    router.push(`/booking?vertical=tailor_made&destination=${encodeURIComponent(name)}`);
  };

  return (
    <div className="min-h-screen bg-[var(--background)] text-[var(--foreground)]">
      <WorldwayNav />

      {/* Hero */}
      <section className="relative pt-24 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#050810] via-[var(--background)] to-[#0D1525]" />
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(ellipse 80% 60% at 50% 20%, rgba(201,168,76,0.15) 0%, transparent 70%)' }} />
        <div className="relative z-10 max-w-5xl mx-auto px-6">
          <nav className="flex items-center gap-2 text-xs text-[var(--muted-foreground)] mb-6">
            <Link href="/" className="hover:text-[var(--primary)] transition-colors">Home</Link>
            <span>/</span>
            <span className="text-[var(--foreground)]">Small Group Journeys</span>
          </nav>
          <p className="label-caps mb-3 text-[var(--primary)]">Journeys · Small Group</p>
          <h1 className="text-4xl md:text-6xl font-bold mb-4" style={{ fontFamily: 'var(--font-serif)' }}>
            Travel Together,<br />
            <span className="gold-text-gradient">Arrive Transformed.</span>
          </h1>
          <p className="text-lg text-[var(--muted-foreground)] mb-8 max-w-xl">
            Intimate groups of up to 12 guests. Expert local guides. Luxury accommodation throughout. Departures to the world&apos;s most extraordinary destinations.
          </p>
          <div className="flex flex-wrap gap-3">
            <button onClick={() => router.push('/booking?vertical=tailor_made')} className="btn-primary px-8 py-3 rounded-xl text-sm font-semibold">
              View All Departures
            </button>
            <Link href="/tailor-made" className="px-8 py-3 rounded-xl text-sm font-semibold border border-[var(--border)] text-[var(--muted-foreground)] hover:border-[var(--primary)]/50 hover:text-[var(--primary)] transition-all">
              Prefer Tailor-Made?
            </Link>
          </div>
        </div>
      </section>

      {/* Why Small Group */}
      <section className="py-16 border-t border-[var(--border)]">
        <div className="max-w-5xl mx-auto px-6">
          <p className="label-caps mb-2">Why Small Group</p>
          <h2 className="text-2xl font-bold mb-8" style={{ fontFamily: 'var(--font-serif)' }}>The <span className="gold-text-gradient">WorldwayLuxe Difference</span></h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((f) => (
              <div key={f.title} className="glass-card rounded-xl p-5">
                <div className="text-3xl mb-3">{f.icon}</div>
                <h3 className="text-sm font-bold mb-2" style={{ fontFamily: 'var(--font-serif)' }}>{f.title}</h3>
                <p className="text-xs text-[var(--muted-foreground)] leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Departures */}
      <section className="py-16 border-t border-[var(--border)]">
        <div className="max-w-5xl mx-auto px-6">
          <p className="label-caps mb-2">Upcoming Departures</p>
          <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'var(--font-serif)' }}>Curated <span className="gold-text-gradient">Group Journeys</span></h2>

          {/* Region filter */}
          <div className="flex gap-2 overflow-x-auto pb-3 mb-8 scrollbar-hide">
            {REGIONS.map((r) => (
              <button key={r} onClick={() => setActiveRegion(r)} className={`flex-shrink-0 px-4 py-1.5 rounded-lg text-xs font-medium transition-all ${activeRegion === r ? 'bg-[var(--primary)] text-[var(--primary-foreground)]' : 'text-[var(--muted-foreground)] hover:text-[var(--foreground)] border border-[var(--border)]'}`}>{r}</button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((d) => (
              <div key={d.name} className="glass-card rounded-xl p-5 hover:border-[rgba(201,168,76,0.3)] transition-all group">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] uppercase tracking-widest text-[var(--primary)] font-medium">{d.region}</span>
                  <span className="text-[10px] text-[var(--muted-foreground)] bg-[var(--surface-3)] px-2 py-0.5 rounded-full">{d.groupSize}</span>
                </div>
                <h3 className="text-base font-bold mb-1 group-hover:text-[var(--primary)] transition-colors" style={{ fontFamily: 'var(--font-serif)' }}>{d.name}</h3>
                <div className="flex items-center gap-3 text-xs text-[var(--muted-foreground)] mb-3">
                  <span>⏱ {d.duration}</span>
                  <span>📅 {d.departure}</span>
                </div>
                <div className="flex flex-wrap gap-1 mb-4">
                  {d.tags.map((tag) => (
                    <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-[rgba(201,168,76,0.1)] text-[var(--primary)]">{tag}</span>
                  ))}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold text-[var(--primary)]">{d.price}</span>
                  <button onClick={() => handleBook(d.name)} className="text-xs px-4 py-1.5 rounded-lg bg-[var(--primary)]/10 text-[var(--primary)] hover:bg-[var(--primary)]/20 transition-colors font-medium">
                    Enquire →
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 border-t border-[var(--border)]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="glass-card rounded-2xl p-8 border border-[rgba(201,168,76,0.2)] text-center">
            <p className="label-caps mb-2 text-[var(--primary)]">Custom Departure</p>
            <h3 className="text-2xl font-bold mb-3" style={{ fontFamily: 'var(--font-serif)' }}>Don&apos;t See Your Ideal Journey?</h3>
            <p className="text-sm text-[var(--muted-foreground)] mb-6 max-w-md mx-auto">We can arrange a private group departure for your family, friends or corporate team — any destination, any date.</p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link href="/ai-concierge" className="btn-primary px-8 py-3 rounded-xl text-sm font-semibold">Speak to Concierge</Link>
              <Link href="/tailor-made" className="px-8 py-3 rounded-xl text-sm font-semibold border border-[var(--border)] text-[var(--muted-foreground)] hover:border-[var(--primary)]/50 hover:text-[var(--primary)] transition-all">Tailor-Made Journeys</Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-[var(--border)] py-8">
        <div className="max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-xs text-[var(--muted-foreground)]">© 2026 WorldwayLuxe · All rights reserved.</div>
          <div className="flex items-center gap-6 text-xs text-[var(--muted-foreground)]">
            <Link href="/tailor-made" className="hover:text-[var(--primary)] transition-colors">Tailor-Made</Link>
            <Link href="/safari" className="hover:text-[var(--primary)] transition-colors">Safari</Link>
            <Link href="/polar-expeditions" className="hover:text-[var(--primary)] transition-colors">Polar Expeditions</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
